/**
 * Localization, handled CENTRALLY — never per component (CLAUDE.md §12).
 *
 * Three rules this module exists to enforce:
 *   1. Storage is UTC; display is Jalali (Shamsi). Conversion happens here.
 *   2. Persian digits are a *presentation* form, applied at format time and
 *      never stored or sent back to the API.
 *   3. Phone numbers and other LTR islands are marked as such by the caller
 *      (dir="ltr"), because an RTL page mangles them otherwise.
 */
import {
  addMonths as addJalaliMonthsFn,
  format as formatJalali,
  isSameMonth as isSameJalaliMonth,
  parse as parseJalali,
  startOfMonth as startOfJalaliMonth,
} from 'date-fns-jalali';

const PERSIAN_DIGITS = ['۰', '۱', '۲', '۳', '۴', '۵', '۶', '۷', '۸', '۹'];

/** Presentation only — never feed the result back into the API. */
export function toPersianDigits(value) {
  if (value === null || value === undefined) return '';
  return String(value).replace(/\d/g, (d) => PERSIAN_DIGITS[Number(d)]);
}

/**
 * The inverse: Persian (۰-۹) and Arabic-Indic (٠-٩) digits back to ASCII.
 *
 * Needed the moment a user *types* rather than reads — a Persian keyboard
 * produces «۰۹۱۲», and every downstream consumer (the API, Number(), a date
 * parser) speaks ASCII. Applied at the input edge so nothing else has to.
 */
export function fromPersianDigits(value) {
  if (value === null || value === undefined) return '';
  return String(value).replace(/[۰-۹٠-٩]/g, (d) => {
    const code = d.charCodeAt(0);
    const base = code >= 0x0660 && code <= 0x0669 ? 0x0660 : 0x06f0;
    return String(code - base);
  });
}

/** Parse an API timestamp (ISO-8601 UTC). Returns null for missing/invalid. */
function parse(iso) {
  if (!iso) return null;
  const date = new Date(iso);
  return Number.isNaN(date.getTime()) ? null : date;
}

/** Jalali date, Persian digits: ۱۴۰۵/۰۶/۰۸ */
export function formatDate(iso) {
  const date = parse(iso);
  return date ? toPersianDigits(formatJalali(date, 'yyyy/MM/dd')) : '—';
}

/** Jalali date + time: ۱۴۰۵/۰۶/۰۸ ۱۴:۳۰ */
export function formatDateTime(iso) {
  const date = parse(iso);
  return date ? toPersianDigits(formatJalali(date, 'yyyy/MM/dd HH:mm')) : '—';
}

/** Jalali long form: ۸ شهریور ۱۴۰۵ */
export function formatDateLong(iso) {
  const date = parse(iso);
  return date ? toPersianDigits(formatJalali(date, 'd MMMM yyyy')) : '—';
}

/** Relative phrasing for a timeline; falls back to an absolute date. */
export function formatRelative(iso) {
  const date = parse(iso);
  if (!date) return '—';
  const days = Math.round((Date.now() - date.getTime()) / 86_400_000);
  if (days === 0) return 'امروز';
  if (days === 1) return 'دیروز';
  if (days > 1 && days < 30) return `${toPersianDigits(days)} روز پیش`;
  if (days < 0 && days > -30) return `${toPersianDigits(-days)} روز دیگر`;
  return formatDate(iso);
}

/** Phone display: Persian digits, but the caller must render it dir="ltr". */
export function formatPhone(number) {
  return toPersianDigits(number ?? '');
}

export function formatCount(value) {
  return toPersianDigits(value ?? 0);
}

/** Bytes as «۱٫۲ مگابایت». Presentation only. */
export function formatBytes(bytes) {
  const size = Number(bytes ?? 0);
  if (size >= 1024 * 1024) return `${toPersianDigits((size / 1048576).toFixed(1))} مگابایت`;
  if (size >= 1024) return `${toPersianDigits(Math.round(size / 1024))} کیلوبایت`;
  return `${toPersianDigits(size)} بایت`;
}

/* ---------- Jalali <-> ISO, for INPUTS rather than display ----------
 *
 * Reading a date is `formatDate`; typing one needs the round trip. The rule
 * from CLAUDE.md §12 holds in both directions: storage is UTC Gregorian, the
 * user only ever sees and types Jalali, and the conversion happens here so no
 * component grows its own calendar.
 */

/** Jalali `yyyy/MM/dd` (Persian or ASCII digits) -> ISO `YYYY-MM-DD`, or null. */
export function jalaliToIsoDate(input) {
  const text = fromPersianDigits(input ?? '').trim().replace(/-/g, '/');
  if (!/^\d{4}\/\d{1,2}\/\d{1,2}$/.test(text)) return null;
  const date = parseJalali(text, 'yyyy/M/d', new Date());
  if (Number.isNaN(date.getTime())) return null;
  // Format from the Gregorian side: a plain toISOString() would shift the day
  // for anyone east or west of UTC, which is exactly where the users are.
  const pad = (n) => String(n).padStart(2, '0');
  return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}`;
}

/** ISO `YYYY-MM-DD` -> Jalali `۱۴۰۵/۰۶/۰۸` for an input's value. */
export function isoToJalaliDate(iso) {
  if (!iso) return '';
  // Parsed as local midnight, not UTC, for the same reason as above.
  const [y, m, d] = String(iso).slice(0, 10).split('-').map(Number);
  if (!y || !m || !d) return '';
  return toPersianDigits(formatJalali(new Date(y, m - 1, d), 'yyyy/MM/dd'));
}

/**
 * An ISO date (`YYYY-MM-DD`) + `HH:mm` -> an ISO-8601 UTC instant, or null.
 *
 * Takes an ISO date rather than a Jalali one because that is what
 * `JalaliDateField` emits: the field owns the calendar conversion, and this
 * only adds the time and the timezone.
 */
export function isoDateAndTimeToInstant(isoDate, timeInput) {
  if (!isoDate) return null;
  const time = fromPersianDigits(timeInput ?? '').trim() || '00:00';
  if (!/^\d{1,2}:\d{2}$/.test(time)) return null;
  const [hours, minutes] = time.split(':').map(Number);
  if (hours > 23 || minutes > 59) return null;
  const [y, m, d] = String(isoDate).split('-').map(Number);
  // Local time, then toISOString: the user means «۱۴:۳۰ here», not 14:30 UTC.
  return new Date(y, m - 1, d, hours, minutes).toISOString();
}

/** A Jalali date + `HH:mm` -> an ISO-8601 UTC instant, or null. */
export function jalaliDateTimeToIso(dateInput, timeInput) {
  return isoDateAndTimeToInstant(jalaliToIsoDate(dateInput), timeInput);
}

/**
 * An ISO instant -> the two values a date+time pair of inputs holds:
 * an ISO date for `JalaliDateField`, and `HH:mm` local time.
 */
export function isoToDateAndTime(iso) {
  const date = parse(iso) ?? new Date();
  const pad = (n) => String(n).padStart(2, '0');
  return {
    date: `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}`,
    time: `${pad(date.getHours())}:${pad(date.getMinutes())}`,
  };
}

/** Persian labels for the pipeline stages. Distinct from subscription type. */
export const COLLABORATION_STATUS_LABELS = {
  UNKNOWN: 'نامعلوم',
  INTERESTED: 'مشتاق اشتراک',
  CONSULTING: 'مشاوره',
  SUBSCRIBER: 'مشترک',
};

/**
 * Persian labels for gender.
 *
 * Here rather than in a screen because two now need it — the profile's edit
 * form and the search panel — and CLAUDE.md §12 puts every enum label in this
 * module precisely so the second reader does not retype the first one's words.
 */
export const GENDER_LABELS = { MALE: 'مرد', FEMALE: 'زن', OTHER: 'سایر' };

/** Persian labels for the commercial arrangement. NOT the pipeline stage. */
export const SUBSCRIPTION_TYPE_LABELS = {
  WHITELABEL: 'وایت‌لیبل',
  STANDARD: 'عادی',
  BARTER: 'تهاتر',
};

export const INTERACTION_STATUS_LABELS = {
  PLANNED: 'برنامه‌ریزی‌شده',
  DONE: 'انجام‌شده',
  CANCELLED: 'لغوشده',
};

export const CONTACT_KIND_LABELS = {
  PERSON: 'شخص',
  ORGANIZATION: 'سازمان',
};

/* ---------- the calendar week (Step 4) ----------
 *
 * In Iran the week runs SATURDAY to FRIDAY, and Friday is the weekend. Every
 * date library in the JS ecosystem defaults to a Sunday or Monday start, so
 * getting this wrong is the default outcome rather than an unlucky one — which
 * is why it lives here, once, rather than in the calendar component.
 */

/** Jalali weekday names, in the order a Persian week is read: شنبه first. */
export const WEEKDAYS_FA = [
  'شنبه',
  'یک‌شنبه',
  'دوشنبه',
  'سه‌شنبه',
  'چهارشنبه',
  'پنج‌شنبه',
  'جمعه',
];

/** Friday. Rendered differently, not hidden — meetings do happen on it. */
export const WEEKEND_INDEX = 6;

/**
 * The one-letter column heads of a month grid, Saturday first: ش ی د س چ پ ج.
 *
 * DERIVED from the names above rather than retyped, so a fix to «پنج‌شنبه»
 * cannot leave the picker spelling it the old way. All seven first letters
 * happen to be distinct in Persian — «شنبه» is ش and «سه‌شنبه» is س — which is
 * why every Persian wall calendar uses exactly these.
 */
export const PERSIAN_WEEKDAY_INITIALS = WEEKDAYS_FA.map((name) => name.slice(0, 1));

/**
 * Jalali month names, in order.
 *
 * Here rather than in a screen for the same reason the weekday names are: an
 * occasion picker, a report's bucket label and a date range all need them, and
 * three copies is three chances for «مُرداد» to be spelled two ways.
 */
export const PERSIAN_MONTHS = [
  'فروردین',
  'اردیبهشت',
  'خرداد',
  'تیر',
  'مرداد',
  'شهریور',
  'مهر',
  'آبان',
  'آذر',
  'دی',
  'بهمن',
  'اسفند',
];

/**
 * An ISO date -> «شهریور ۱۴۰۵», for a report's monthly bucket.
 *
 * The SERVER sends ISO dates deliberately — a calendar is a locale decision and
 * the domain services do not make locale decisions (CLAUDE.md §12) — so the
 * conversion happens here, in the one module that owns dates.
 */
export function formatJalaliMonth(iso) {
  if (!iso) return '';
  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) return '';
  const parts = Object.fromEntries(
    new Intl.DateTimeFormat('en-u-ca-persian-nu-latn', {
      year: 'numeric',
      month: 'numeric',
      timeZone: 'UTC',
    })
      .formatToParts(date)
      .map((part) => [part.type, part.value]),
  );
  const month = PERSIAN_MONTHS[Number(parts.month) - 1] ?? '';
  return `${month} ${toPersianDigits(parts.year)}`;
}

/**
 * How far a JS date is into the Persian week.
 *
 * `Date.getDay()` is 0=Sunday…6=Saturday. Saturday must map to 0 and Friday to
 * 6, which is `(getDay() + 1) % 7`.
 */
export function persianWeekday(date) {
  return (date.getDay() + 1) % 7;
}

/** Local midnight on the Saturday of `date`'s week. */
export function startOfPersianWeek(date) {
  const start = new Date(date);
  start.setHours(0, 0, 0, 0);
  start.setDate(start.getDate() - persianWeekday(start));
  return start;
}

/** The seven days of `date`'s week, Saturday first, each at local midnight. */
export function persianWeekDays(date) {
  const start = startOfPersianWeek(date);
  return Array.from({ length: 7 }, (_, index) => {
    const day = new Date(start);
    day.setDate(start.getDate() + index);
    return day;
  });
}

/** Local midnight on `date`. */
export function startOfDay(date) {
  const start = new Date(date);
  start.setHours(0, 0, 0, 0);
  return start;
}

export function addDays(date, days) {
  const out = new Date(date);
  out.setDate(out.getDate() + days);
  return out;
}

/** «۸ شهریور» — a column header, so no year. */
export function formatDayHeader(date) {
  return toPersianDigits(formatJalali(date, 'd MMMM'));
}

/** «شهریور ۱۴۰۵» — the range title above a week. */
export function formatMonthYear(date) {
  return toPersianDigits(formatJalali(date, 'MMMM yyyy'));
}

/**
 * «۸ – ۱۴ شهریور ۱۴۰۵», or both months when the week straddles one.
 *
 * A week that crosses a month boundary reading «۲۹ – ۴ شهریور» would be simply
 * wrong, and Persian months are short enough that it happens most months.
 */
export function formatWeekRange(date) {
  const days = persianWeekDays(date);
  const first = days[0];
  const last = days[6];
  const sameMonth = formatJalali(first, 'MM yyyy') === formatJalali(last, 'MM yyyy');
  if (sameMonth) {
    return toPersianDigits(
      `${formatJalali(first, 'd')} – ${formatJalali(last, 'd MMMM yyyy')}`,
    );
  }
  return toPersianDigits(
    `${formatJalali(first, 'd MMMM')} – ${formatJalali(last, 'd MMMM yyyy')}`,
  );
}

/**
 * The Gregorian face of a Jalali date — «۲۹ آگوست ۲۰۲۶» / «29 Aug 2026».
 *
 * Shown BESIDE the Jalali date in the calendar, never instead of it. A Persian
 * user books in Shamsi and a congress invitation, a Google Calendar entry or a
 * foreign supplier's email arrives in Gregorian, and translating between the
 * two in one's head is the small daily tax this removes.
 *
 * `en-GB` and Latin digits deliberately: the whole point is to read the date
 * the way the document that used it wrote it, and «۲۹ اوت» would be a third
 * form nobody asked for.
 */
export function formatGregorian(date, { withYear = true } = {}) {
  const value = date instanceof Date ? date : parse(date);
  if (!value) return '';
  return new Intl.DateTimeFormat('en-GB', {
    day: 'numeric',
    month: 'short',
    ...(withYear ? { year: 'numeric' } : {}),
  }).format(value);
}

/* -------------------------------------------------------------------------
 * The Jalali month grid — everything a date PICKER needs.
 *
 * Here rather than in the picker component for the same reason the weekday
 * names are here: a calendar is a locale decision, and CLAUDE.md §12 says
 * exactly one module makes those. A picker that did its own month arithmetic
 * would be a second calendar, and the two would disagree about Esfand 30 in a
 * leap year long before anybody noticed.
 * ------------------------------------------------------------------------- */

/** Move `n` Jalali months from an ISO date, clamped by the library. */
export function addJalaliMonths(iso, n) {
  const base = isoToLocalDate(iso) ?? new Date();
  return toIsoDate(addJalaliMonthsFn(base, n));
}

/** «شهریور ۱۴۰۵» for the header above a month grid. */
export function jalaliMonthTitle(iso) {
  const base = isoToLocalDate(iso) ?? new Date();
  return formatMonthYear(base);
}

/**
 * Six weeks of cells for the Jalali month containing `iso`, Saturday first.
 *
 * Always six rows, so the grid does not change height as the user pages
 * through months — a picker that grows and shrinks moves the button underneath
 * it out from under the cursor.
 */
export function jalaliMonthGrid(iso, { today = new Date() } = {}) {
  const base = isoToLocalDate(iso) ?? startOfDay(today);
  const first = startOfJalaliMonth(base);
  const gridStart = startOfPersianWeek(first);

  const weeks = [];
  const cursor = new Date(gridStart);
  for (let week = 0; week < 6; week += 1) {
    const row = [];
    for (let day = 0; day < 7; day += 1) {
      row.push({
        iso: toIsoDate(cursor),
        label: toPersianDigits(formatJalali(cursor, 'd')),
        inMonth: isSameJalaliMonth(cursor, first),
        isToday: isSameDay(cursor, today),
        isWeekend: persianWeekday(cursor) === WEEKEND_INDEX,
      });
      cursor.setDate(cursor.getDate() + 1);
    }
    weeks.push(row);
  }
  return weeks;
}

/** An ISO `yyyy-mm-dd` as a LOCAL date, never UTC — the same rule as
 *  `isoToJalaliDate`, which is why a date typed at 00:30 does not slip a day.
 *  Exported because the calendar converts in both directions when it jumps to a
 *  picked date, and a second implementation of this is a day-boundary bug. */
export function isoToLocalDate(iso) {
  if (!iso) return null;
  const [y, m, d] = String(iso).slice(0, 10).split('-').map(Number);
  if (!y || !m || !d) return null;
  return new Date(y, m - 1, d);
}

/** A local Date back to `yyyy-mm-dd`, without going through UTC. */
export function toIsoDate(date) {
  const y = date.getFullYear();
  const m = String(date.getMonth() + 1).padStart(2, '0');
  const d = String(date.getDate()).padStart(2, '0');
  return `${y}-${m}-${d}`;
}

/** Today, as the ISO date a picker should open on. */
export function todayIsoDate() {
  return toIsoDate(new Date());
}

/** `HH:mm` in Persian digits, from an ISO instant. */
export function formatTime(iso) {
  const date = parse(iso);
  return date ? toPersianDigits(formatJalali(date, 'HH:mm')) : '—';
}

/** True when two dates fall on the same local day. */
export function isSameDay(a, b) {
  return (
    a.getFullYear() === b.getFullYear() &&
    a.getMonth() === b.getMonth() &&
    a.getDate() === b.getDate()
  );
}

/** Persian labels for the activity lifecycle. */
export const ACTIVITY_STATUS_LABELS = {
  SCHEDULED: 'برنامه‌ریزی‌شده',
  DONE: 'انجام‌شده',
  CANCELLED: 'لغوشده',
  MISSED: 'انجام‌نشده',
};

/** Where a reminder is delivered. Email/WhatsApp are roadmap (CLAUDE.md §6). */
export const REMINDER_CHANNEL_LABELS = {
  IN_APP: 'در برنامه',
  GOOGLE: 'گوگل',
};
